import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import SEO from '../components/common/SEO';
import VendorVerificationBadge from '../components/common/VendorVerificationBadge';
import './VendorDetails.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const fallbackImages = {
  banner: '/assets/img/breadcrumb/vendor-banner.jpg',
  logo: '/mangoloops-fevicon.png',
  holiday: '/assets/img/hotels/hotels-places/del-sm.webp',
  hotel: '/assets/img/hotels/hotels-places/del-sm.webp',
  cab: '/assets/img/hotels/hotels-places/del-sm.webp',
  medical: '/assets/img/medical-tourism/banner.jpg',
};

const parseCategories = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return String(value).split(',').map(item => item.trim()).filter(Boolean);
  }
};

const normalizeImage = (image, fallback) => {
  if (!image) return fallback;
  if (/^https?:\/\//i.test(image)) return image;
  return image.startsWith('/') ? image : `/${image}`;
};

const slugify = (value) => value
  ? String(value).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-').replace(/^-+|-+$/g, '')
  : '';

const toTitleCase = (value) => String(value)
  .toLocaleLowerCase()
  .replace(/\b\w/g, character => character.toLocaleUpperCase());

const createDetailSlug = (...parts) => parts.filter(part => part !== null && part !== undefined && part !== '')
  .map(part => typeof part === 'number' ? part : slugify(part))
  .filter(Boolean)
  .join('-');

const parseSocialLinks = (value) => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try { return JSON.parse(value) || {}; } catch { return {}; }
};

const socialPlatforms = [
  ['instagram', 'Instagram', 'bi-instagram'],
  ['facebook', 'Facebook', 'bi-facebook'],
  ['linkedin', 'LinkedIn', 'bi-linkedin'],
  ['twitter', 'X / Twitter', 'bi-twitter-x'],
  ['youtube', 'YouTube', 'bi-youtube'],
  ['pinterest', 'Pinterest', 'bi-pinterest'],
];

const getLocation = (vendor) => {
  return [vendor?.operational_city || vendor?.company_city, vendor?.operational_state || vendor?.company_state, 'India']
    .filter(Boolean)
    .join(', ');
};

const Section = ({ title, children, show = true }) => {
  if (!show) return null;
  return (
    <section className="vendor-profile-block">
      <h2>{title}</h2>
      {children}
    </section>
  );
};

const ScrollCards = ({ items, type, fallbackImage }) => (
  <div className="vendor-scroll-wrapper">
    <div className="vendor-scrolling-row">
      {items.map(item => (
        <Link to={item.link || '#'} className={`vendor-service-card ${type}`} key={`${type}-${item.id || item.title}`}>
          <div className="vendor-card-img">
            <img src={normalizeImage(item.image, fallbackImage)} alt={item.title} />
          </div>
          <div className="vendor-card-title">{item.title}</div>
          {item.subtitle && <div className="vendor-card-subtitle">{item.subtitle}</div>}
        </Link>
      ))}
    </div>
  </div>
);

function VendorDetails() {
  const { company_slug, encoded_id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(Boolean(encoded_id));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!encoded_id) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError('');

    const profilePath = company_slug
      ? `${encodeURIComponent(company_slug)}/${encodeURIComponent(encoded_id)}`
      : encodeURIComponent(encoded_id);

    axios.get(`${API_BASE_URL}/vendors/public-profile/${profilePath}`)
      .then((response) => {
        if (isMounted) setProfile(response.data);
      })
      .catch(() => {
        if (isMounted) setError('Vendor profile could not be loaded.');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [company_slug, encoded_id]);

  const vendor = profile?.vendor || {};
  const kycHistory = profile?.kycHistory || [];
  const services = profile?.services || {};
  const categories = useMemo(() => parseCategories(vendor.business_categories), [vendor.business_categories]);
  const socialLinks = useMemo(() => parseSocialLinks(vendor.social_media_links), [vendor.social_media_links]);
  const holidayCards = (services.holidayPackages || []).map(item => ({
    id: item.id,
    title: item.package_name || item.city || 'Holiday Package',
    subtitle: item.city,
    image: item.image,
    link: `/holiday-details/${createDetailSlug(
      item.package_name,
      item.city,
      Math.round(item.offer_price || item.package_price || 0),
      item.id
    )}`,
  }));

  const hotelCards = (services.hotels || []).map(item => ({
    id: item.id,
    title: item.hotel_name || item.city || 'Hotel',
    subtitle: item.city,
    image: item.image,
    link: `/hotel-details/${createDetailSlug(
      item.hotel_name,
      item.city,
      Math.round(item.offer_price || item.hotel_price || 0),
      item.id
    )}`,
  }));

  const cabCards = (services.cabs || []).map(item => ({
    id: item.id,
    title: item.brand_name || 'Cab Service',
    subtitle: [item.cab_city, item.accommodation_type, item.seating_capacity && `${item.seating_capacity} Seats`].filter(Boolean).join(' | '),
    image: item.image,
  }));

  const yatraCards = (services.yatras || []).map(item => ({
    id: item.id,
    title: item.yatra_name || 'Yatra Package',
    subtitle: item.city,
    image: item.image,
    link: `/yatra-details/${createDetailSlug(
      item.yatra_name,
      item.city,
      Math.round(item.offer_price || item.yatra_price || 0),
      item.id
    )}`,
  }));

  const hospitalCards = (services.hospitals || []).map(item => ({
    id: item.id,
    title: item.hospital_name || 'Hospital',
    subtitle: [item.city, item.state].filter(Boolean).join(', '),
    image: item.image,
    link: `/medical-details/hospital/${createDetailSlug(item.hospital_name, item.city, item.id)}`,
  }));

  const doctorCards = (services.doctors || []).map(item => ({
    id: item.id,
    title: item.doctor_name || 'Doctor',
    subtitle: [item.city, item.total_experience && `${item.total_experience} Exp.`].filter(Boolean).join(' | '),
    image: item.image,
    link: `/medical-details/doctor/${createDetailSlug(item.doctor_name, item.city, item.id)}`,
  }));
  const medicalCards = [...hospitalCards, ...doctorCards];

  const companyName = toTitleCase(vendor.company_name || vendor.name || 'Vendor Profile');
  const address = vendor.operational_address || vendor.company_address || 'Address details will be updated soon.';
  const bannerImage = normalizeImage(vendor.company_banner_path, fallbackImages.banner);
  const logoImage = normalizeImage(vendor.company_logo_path, fallbackImages.logo);
  const aboutText = vendor.about_company || vendor.company_description || vendor.description || vendor.company_info || 'No company information has been added yet.';

  return (
    <div>
      <SEO title={`${companyName} | Vendor Details | Mangoloops`} description={`View ${companyName} services, address, and contact details on Mangoloops.`} />
      <Header />

      <div className="vendor-profile-page">
        {loading ? (
          <div className="vendor-state">Loading vendor profile...</div>
        ) : error ? (
          <div className="vendor-state">{error}</div>
        ) : (
          <>
            <div className="vendor-hero">
              <img src={bannerImage} alt={`${companyName} banner`} onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = fallbackImages.banner;
              }} />
            </div>

            <div className="vendor-profile-section">
                <div className="vendor-profile-card">
                <div className="vendor-logo-wrapper">
                  <img src={logoImage} alt={`${companyName} logo`} />
                </div>
                <div className="vendor-company-name-row">
                  {vendor.is_publicly_verified && (
                    <i
                      className="bi bi-patch-check-fill vendor-company-verified-icon"
                      aria-label="Verified vendor"
                      title="Verified vendor"
                    />
                  )}
                  <h1>{companyName}</h1>
                </div>
                <p className="vendor-location"><i className="bi bi-geo-alt-fill" aria-hidden="true"></i>{getLocation(vendor)}</p>
                <p className="vendor-address">{address}</p>

                {categories.length > 0 && (
                  <div className="vendor-category-pills">
                    {categories.map(category => <span key={category}>{category}</span>)}
                  </div>
                )}

                <div className="vendor-contact-icons" aria-label="Contact and social links">
                  <VendorVerificationBadge
                    vendor={vendor}
                    kycHistory={kycHistory}
                    className="vendor-profile-verification"
                  />
                  {vendor.whatsapp_no && <a className="social-icon whatsapp" href={`https://wa.me/${vendor.whatsapp_no}`} aria-label="WhatsApp"><i className="bi bi-whatsapp"></i></a>}
                  {(vendor.phone || vendor.mobile) && <a className="social-icon phone" href={`tel:${vendor.phone || vendor.mobile}`} aria-label="Phone"><i className="bi bi-telephone-fill"></i></a>}
                  {vendor.email && <a className="social-icon email" href={`mailto:${vendor.email}`} aria-label="Email"><i className="bi bi-envelope-fill"></i></a>}
                  {socialPlatforms.map(([key, label, icon]) => socialLinks[key] ? (
                    <a
                      key={key}
                      href={/^https?:\/\//i.test(socialLinks[key]) ? socialLinks[key] : `https://${socialLinks[key]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      title={label}
                      className={`social-icon ${key}`}
                    >
                      <i className={`bi ${icon}`} aria-hidden="true"></i>
                    </a>
                  ) : null)}
                </div>
              </div>
            </div>

            <main className="vendor-profile-main">
              <Section title="Tours Package" show={holidayCards.length > 0}>
                <ScrollCards items={holidayCards} type="tour" fallbackImage={fallbackImages.holiday} />
              </Section>

              <Section title="Cabs Services" show={cabCards.length > 0}>
                <div className="vendor-cabs-grid">
                  {cabCards.map(item => (
                    <div className="vendor-cab-card" key={`cab-${item.id}`}>
                      <img src={normalizeImage(item.image, fallbackImages.cab)} alt={item.title} />
                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.subtitle || 'Comfortable cab service with vendor support.'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Hotel Services" show={hotelCards.length > 0}>
                <ScrollCards items={hotelCards} type="hotel" fallbackImage={fallbackImages.hotel} />
              </Section>

              <Section title="Yatra Services" show={yatraCards.length > 0}>
                <ScrollCards items={yatraCards} type="yatra" fallbackImage="/no_image_teerth-yatra.jpg" />
              </Section>

              <Section title="Medical Tourism" show={medicalCards.length > 0}>
                <ScrollCards items={medicalCards} type="medical" fallbackImage={fallbackImages.medical} />
              </Section>

              <Section title="About Company">
                <p className="vendor-about-company">{aboutText}</p>
              </Section>
            </main>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default VendorDetails;
